"use strict";
/* tslint:disable:max-classes-per-file */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Hub = exports.OIDCService = exports.parseClaims = void 0;
const axios_1 = require("axios");
const oidc_client_1 = require("@ftdr/oidc-client");
function setCachedWindowLocation(nickname) {
    localStorage.setItem(`cached_window_location_${nickname}`, window.location.href);
}
function getCachedWindowLocation(nickname) {
    return localStorage.getItem(`cached_window_location_${nickname}`);
}
// setLastLogin is used to set which oauth client initiated the last login flow
function setLastLogin(nickname) {
    localStorage.setItem(`last_login`, nickname);
}
// getLastLogin is used to get which oauth client initiated the last login flow
function getLastLogin() {
    return localStorage.getItem(`last_login`);
}
function parseClaims(jwt) {
    const tokenSplit = jwt.split('.');
    if (tokenSplit.length < 2) {
        return { aud: '', email: '', exp: 0, iat: 0, jti: '', nbf: '', sub: '', iss: '', roles: [], tid: '' };
    }
    return JSON.parse(atob(tokenSplit[1]));
}
exports.parseClaims = parseClaims;
function getAccessTokenRoles(jwt) {
    var _a;
    try {
        return (_a = parseClaims(jwt)) === null || _a === void 0 ? void 0 : _a.roles;
    }
    catch (e) {
        console.error('Parsing access token while getting roles');
        return undefined;
    }
}
function toExtendedUser(u) {
    var _a;
    return {
        toStorageString() {
            return u.toStorageString();
        },
        access_token: u.access_token,
        expired: u.expired,
        expires_at: u.expires_at,
        expires_in: u.expires_in,
        id_token: u.id_token,
        profile: Object.assign(Object.assign({}, u.profile), { roles: ((_a = u.profile) === null || _a === void 0 ? void 0 : _a.roles) || getAccessTokenRoles(u.access_token) }),
        refresh_token: u.refresh_token,
        scope: u.scope,
        scopes: u.scopes,
        session_state: u.session_state,
        state: u.state,
        token_type: u.token_type,
        extra: {
            version: '1.5.3',
        },
    };
}
// OIDCService manages the oidc PKCE flow for SPA clients. It includes an HTTP client that adds bearer tokens to outbound requests.
class OIDCService {
    constructor(config) {
        if (!config.scope) {
            config.scope = 'openid email profile';
        }
        this.nickname = config.nickname;
        this.client = axios_1.default.create({});
        this.userManager = new oidc_client_1.UserManager({
            authority: config.authority,
            client_id: config.client_id,
            redirect_uri: config.redirect_uri,
            post_logout_redirect_uri: config.post_logout_redirect_uri,
            response_type: 'code',
            response_mode: 'query',
            scope: config.scope,
            revokeAccessTokenOnSignout: true,
            loadUserInfo: false,
            metadataUrl: config.metadata_uri,
            userStore: new oidc_client_1.WebStorageStateStore({ store: localStorage }),
            extraQueryParams: config.extraQueryParams || {},
            clockSkew: 3900 // hour & 5 minutes to allow for system clocks that are off by more than an hour
        });
        if (config.debug === true) {
            oidc_client_1.Log.logger = console;
        }
    }
    // getUser gets the user from session storage
    getUser() {
        return this.userManager.getUser().then((u) => {
            if (u) {
                return toExtendedUser(u);
            }
            return null;
        });
    }
    // setClient overrides the built in axios instance.
    setClient(client) {
        this.client = client;
    }
    // removeUser removes the user from session storage
    removeUser() {
        return this.userManager.removeUser();
    }
    // metadata returns oidc related metadata
    metadata() {
        return this.userManager.metadataService;
    }
    // revokeAccessToken revokes the current users access token
    revokeAccessToken() {
        return this.userManager.revokeAccessToken();
    }
    // renewToken refreshes the access token if possible
    renewToken() {
        return this.userManager.signinSilent().then((u) => {
            return toExtendedUser(u);
        });
    }
    // login redirects the user to the IDPs authorize url to sign in
    login() {
        setLastLogin(this.nickname);
        setCachedWindowLocation(this.nickname);
        return this.userManager.signinRedirect();
    }
    // signinRedirectCallback should be called in the oauth2 redirect uri before redirecting back to the home-page(for example) of the SPA
    signinRedirectCallback() {
        return this.userManager.signinRedirectCallback().then((u) => {
            return toExtendedUser(u);
        });
    }
    // logout logs the current user out
    logout() {
        this.userManager.clearStaleState().then(() => {
            oidc_client_1.Log.debug('cleared stale state');
        });
        return this.userManager.signoutRedirect();
    }
    // settings returns the userManager settings
    settings() {
        return this.userManager.settings;
    }
    // handleRequest adds the access token/id token from session_storage before sending the axios http request.
    // useIdToken, if true, sets the Authorization header with the users id token instead of the access token
    handleRequest(useIdToken, config) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.getUser().then((user) => {
                if (user) {
                    if (!config.headers) {
                        config.headers = {};
                    }
                    if (useIdToken) {
                        /* tslint:disable:no-string-literal */
                        config.headers['Authorization'] = `Bearer ${user.id_token}`;
                    }
                    else {
                        /* tslint:disable:no-string-literal */
                        config.headers['Authorization'] = `Bearer ${user.access_token}`;
                    }
                }
            });
            return this.client.request(config);
        });
    }
}
exports.OIDCService = OIDCService;
// Hub is a map of OIDCServices with a method that can be used as a universal callback for all OIDCServices
class Hub {
    constructor(services) {
        const serviceMap = new Map();
        services.forEach((svc) => {
            serviceMap.set(svc.nickname, svc);
        });
        this.serviceMap = serviceMap;
    }
    // this can be used as a universal Oauth2 callback for all registered OIDCServices.
    callback(determineRedirect) {
        const nickname = getLastLogin();
        if (nickname) {
            const svc = this.serviceMap.get(nickname);
            if (svc) {
                return svc.signinRedirectCallback().then((u) => {
                    const windowLoc = getCachedWindowLocation(nickname) || '/';
                    let overrideLoc = null;
                    if (determineRedirect) {
                        overrideLoc = determineRedirect(nickname, windowLoc);
                    }
                    window.location.replace(overrideLoc || windowLoc);
                });
            }
        }
    }
}
exports.Hub = Hub;
